import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { MultiSelect } from "primereact/multiselect";
import * as procService from "../../services/procService";
import { useModule } from "../Platform/ModuleGate";
import { hasModulePermission } from "../../constants/platformModules";
import { ChatPanel } from "./Chat";
import { DataTable, ProcBadge, act, fileSize, linkBtn, openSigned } from "./procUi";
import { AppDropdown, DetailGrid, EmptyState, Field, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, SlidePanel, formatDate, inputStyle, pageStyle, useLoad } from "../../components/ui";

// Suppliers (buyer side): invite a supplier to the supplier portal, follow who accepted, suspend, tag the
// categories each supplier supplies, and review the documents they uploaded (trade licence, tax certificate ...).
// A supplier is always also a supplier in Inventory / Payables: inviting adds it there, or links one you already have.

const STATUS_FILTERS = [
  { label: "All suppliers", value: null },
  { label: "Invited", value: "INVITED" },
  { label: "Active", value: "ACTIVE" },
  { label: "Suspended", value: "SUSPENDED" },
];
const NEW_SUPPLIER = "NEW";

function ComplianceCell({ row }) {
  if (!row.compliance) return <span style={{ color: "#9CA3AF" }}>—</span>;
  const { required, approved, status } = row.compliance;
  return (
    <span className="d-inline-flex align-items-center gap-2">
      <ProcBadge status={status} label={required ? `${approved}/${required} approved` : "None required"} />
    </span>
  );
}

function InviteSent({ result, onClose }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.invite_url);
      toast.success("Link copied");
    } catch {
      toast.error("Copy the link by hand");
    }
  };
  return (
    <SlidePanel show={!!result} onHide={onClose} title="Invitation created" width={460} footer={<PrimaryButton onClick={onClose}>Done</PrimaryButton>}>
      {result ? (
        <>
          <p style={{ fontSize: 14 }}>
            {result.emailed ? "The invitation was emailed to the contact." : "The email could not be sent. Share the link below with the contact yourself."}
          </p>
          <Field label="Invitation link" htmlFor="proc-invite-link" hint={`Works once, until ${formatDate(result.expires_at)}. Anyone with the link can create the supplier account, so share it only with the contact.`}>
            <input id="proc-invite-link" className="form-control" readOnly value={result.invite_url} onFocus={(e) => e.target.select()} style={inputStyle} />
          </Field>
          <SecondaryButton onClick={copy}>Copy link</SecondaryButton>
        </>
      ) : null}
    </SlidePanel>
  );
}

function InviteDrawer({ show, onHide, onSent }) {
  const linkable = useLoad(() => procService.linkableSuppliers(), [show]);
  const options = [{ label: "A new supplier", value: NEW_SUPPLIER }, ...(linkable.data || []).map((s) => ({ label: `${s.code} – ${s.name}`, value: s.id }))];
  return (
    <FormDrawer
      show={show}
      title="Invite a supplier"
      submitLabel="Send invitation"
      initial={{ core_supplier_id: NEW_SUPPLIER }}
      onHide={onHide}
      onSave={(clean) => {
        const { core_supplier_id: chosen, ...rest } = clean;
        return procService.inviteSupplier(chosen && chosen !== NEW_SUPPLIER ? { ...rest, core_supplier_id: chosen } : rest);
      }}
      onDone={onSent}
      fields={(v) => [
        { key: "core_supplier_id", label: "Supplier", type: "select", required: true, options, hint: "Pick a supplier you already buy from, or add a new one" },
        ...(!v.core_supplier_id || v.core_supplier_id === NEW_SUPPLIER ? [{ key: "company_name", label: "Supplier name", required: true, max: 200 }] : []),
        { key: "contact_name", label: "Contact person", required: true, max: 150 },
        { key: "email", label: "Contact email", type: "email", required: true, hint: "The invitation is sent here, and it becomes their sign-in email" },
        { key: "phone", label: "Contact mobile", type: "mobile" },
      ]}
    />
  );
}

function RejectDrawer({ doc, onHide, onDone }) {
  return (
    <FormDrawer
      show={!!doc}
      title="Reject document"
      submitLabel="Reject"
      initial={{}}
      onHide={onHide}
      onSave={(clean) => procService.reviewSupplierDocument(doc.id, { status: "REJECTED", note: clean.note })}
      onDone={onDone}
      fields={[{ key: "note", label: `Why is ${doc ? doc.document_type_name : "the document"} rejected?`, type: "textarea", required: true, max: 500, hint: "The supplier sees this reason" }]}
    />
  );
}

function SupplierPanel({ id, canManage, onClose, onChanged }) {
  const { data, loading, error, reload } = useLoad(() => procService.getSupplier(id), [id]);
  const categories = useLoad(() => procService.listCategories(), []);
  const [picked, setPicked] = useState(null); // category option ids chosen in the multi-select
  const [rejecting, setRejecting] = useState(null);
  const [chatting, setChatting] = useState(false);

  const changed = () => {
    reload();
    onChanged();
  };
  const d = data;
  const link = d && d.link;
  const catOptions = (categories.data || []).map((c) => ({ label: c.name, value: c.id }));
  // Ids are encrypted afresh in every answer, so what the supplier is tagged with is matched to the list by NAME.
  const current = d ? catOptions.filter((o) => d.categories.some((c) => c.name === o.label)).map((o) => o.value) : [];
  const shown = picked || current;

  const status = (next) => act(() => procService.setSupplierStatus(id, next), "Saved").then((r) => r && changed());
  const resend = () => act(() => procService.resendInvite(id)).then((r) => r && changed());
  const revoke = () => act(() => procService.revokeInvite(id)).then((r) => r && (onChanged(), onClose()));
  const saveCategories = () => act(() => procService.setSupplierCategories(id, shown), "Categories saved").then((r) => r && (setPicked(null), changed()));
  const review = (doc, next) => act(() => procService.reviewSupplierDocument(doc.id, { status: next }), "Saved").then((r) => r && changed());

  return (
    <SlidePanel show onHide={onClose} title={d && d.supplier ? d.supplier.name : "Supplier"} width={720}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {d ? (
          <>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <ProcBadge status={link.status} />
              {d.compliance ? <ProcBadge status={d.compliance.status} label={d.compliance.required ? `Documents ${d.compliance.approved}/${d.compliance.required}` : "No documents required"} /> : null}
              <span className="ms-auto d-flex gap-2">
                {canManage && d.company && link.status !== "INVITED" ? <SecondaryButton onClick={() => setChatting(true)}>Message</SecondaryButton> : null}
                {canManage && link.status === "ACTIVE" ? <SecondaryButton onClick={() => status("SUSPENDED")}>Suspend</SecondaryButton> : null}
                {canManage && link.status === "SUSPENDED" ? <PrimaryButton onClick={() => status("ACTIVE")}>Reactivate</PrimaryButton> : null}
                {canManage && link.status === "INVITED" ? (
                  <>
                    <SecondaryButton onClick={revoke}>Withdraw invitation</SecondaryButton>
                    <PrimaryButton onClick={resend}>Send again</PrimaryButton>
                  </>
                ) : null}
              </span>
            </div>
            {link.status === "SUSPENDED" ? <div style={{ fontSize: 13, color: "#991B1B", marginBottom: 12 }}>A suspended supplier cannot be invited to new RFQs. Existing orders carry on.</div> : null}

            <h6>Details</h6>
            <DetailGrid
              items={[
                ["Supplier code", d.supplier && d.supplier.code],
                ["Company (as registered)", d.company && d.company.name],
                ["Registration no.", d.company && d.company.registration_no],
                ["Tax ID", (d.company && d.company.tax_id) || (d.supplier && d.supplier.trn)],
                ["Country", d.company && d.company.country],
                ["Address", (d.company && d.company.address) || (d.supplier && d.supplier.address)],
              ]}
            />

            {d.people.length ? (
              <>
                <h6 className="mt-4">People</h6>
                <DataTable
                  minWidth={480}
                  rowKey={(r) => r.user_id}
                  rows={d.people}
                  columns={[
                    { label: "Name", render: (r) => `${r.first_name}${r.is_primary_user ? " (main user)" : ""}` },
                    { label: "Email", render: (r) => r.email },
                    { label: "Mobile", render: (r) => r.phone_number },
                    { label: "Status", render: (r) => <ProcBadge status={r.status} /> },
                  ]}
                />
              </>
            ) : null}

            <h6 className="mt-4">Categories supplied</h6>
            {canManage ? (
              <div className="d-flex gap-2 align-items-start mb-2">
                <MultiSelect
                  value={shown}
                  options={catOptions}
                  onChange={(e) => setPicked(e.value)}
                  placeholder="No categories yet"
                  display="chip"
                  filter
                  style={{ flex: 1, minHeight: 40, fontSize: 14 }}
                  aria-label="Categories supplied"
                />
                <PrimaryButton onClick={saveCategories} disabled={!picked}>
                  Save
                </PrimaryButton>
              </div>
            ) : (
              <div style={{ fontSize: 14 }}>{d.categories.map((c) => c.name).join(", ") || "None"}</div>
            )}
            <div style={{ fontSize: 12, color: "#6B7280" }}>Used to choose whom to invite to an RFQ. The list is your Inventory categories.</div>

            <h6 className="mt-4">Documents</h6>
            {link.status === "INVITED" ? (
              <div style={{ fontSize: 13, color: "#6B7280" }}>Documents appear here once the supplier has accepted and uploaded them.</div>
            ) : (
              <>
                <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 8 }}>
                  Asked for: {d.requested_documents.map((t) => `${t.name}${t.is_mandatory ? " (required)" : ""}`).join(", ") || "nothing yet (add document types in Procurement Setup)"}.
                </div>
                {d.documents.length === 0 ? (
                  <EmptyState title="No documents uploaded yet" />
                ) : (
                  <DataTable
                    minWidth={560}
                    rows={d.documents}
                    columns={[
                      { label: "Document", render: (r) => r.document_type_name },
                      { label: "File", render: (r) => `${r.file_name} · ${fileSize(r.size)}` },
                      { label: "Expires", render: (r) => (r.expiry_date ? formatDate(r.expiry_date) : "—") },
                      { label: "Review", render: (r) => <ProcBadge status={r.review_status} />, style: { whiteSpace: "nowrap" } },
                      {
                        label: "",
                        render: (r) => (
                          <span className="d-inline-flex gap-2" style={{ whiteSpace: "nowrap" }}>
                            <button type="button" style={linkBtn} onClick={() => openSigned(() => procService.getSupplierDocumentUrl(r.id), toast)}>
                              View
                            </button>
                            {canManage && r.review_status !== "APPROVED" ? (
                              <button type="button" style={linkBtn} onClick={() => review(r, "APPROVED")}>
                                Approve
                              </button>
                            ) : null}
                            {canManage && r.review_status !== "REJECTED" ? (
                              <button type="button" style={linkBtn} onClick={() => setRejecting(r)}>
                                Reject
                              </button>
                            ) : null}
                          </span>
                        ),
                      },
                    ]}
                  />
                )}
              </>
            )}

            {d.invites.length ? (
              <>
                <h6 className="mt-4">Invitations</h6>
                <DataTable
                  minWidth={480}
                  rows={d.invites}
                  columns={[
                    { label: "Sent to", render: (r) => `${r.contact_name} · ${r.email}` },
                    { label: "Sent", render: (r) => formatDate(r.created_at) },
                    { label: "Status", render: (r) => <ProcBadge status={r.status === "ACCEPTED" ? "ACTIVE" : r.status === "REVOKED" ? "INACTIVE" : "INVITED"} label={r.status === "ACCEPTED" ? "Accepted" : r.status === "REVOKED" ? "Withdrawn" : "Waiting"} /> },
                  ]}
                />
              </>
            ) : null}
          </>
        ) : null}
      </LoadState>
      {chatting && d && d.company ? <ChatPanel service={procService} refQuery={{ supplier_id: d.company.id }} title={d.supplier ? d.supplier.name : "Supplier"} canWrite onClose={() => setChatting(false)} /> : null}
      <RejectDrawer doc={rejecting} onHide={() => setRejecting(null)} onDone={changed} />
    </SlidePanel>
  );
}

export default function Suppliers() {
  const { access } = useModule();
  const canManage = hasModulePermission(access, "PROC", "PROC_ADMIN", "PROC_BUYER");
  const [status, setStatus] = useState(null);
  const [typed, setTyped] = useState("");
  const [q, setQ] = useState("");
  const [inviting, setInviting] = useState(false);
  const [sent, setSent] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setQ(typed.trim()), 300);
    return () => clearTimeout(t);
  }, [typed]);

  const { data, loading, error, reload } = useLoad(() => procService.listSuppliers({ status, q }), [status, q]);
  const rows = data || [];

  return (
    <div style={pageStyle}>
      <PageHeader
        title="Suppliers"
        subtitle="Suppliers you invite to quote on your RFQs, and the documents they provide"
        actions={canManage ? <PrimaryButton onClick={() => setInviting(true)}>Invite supplier</PrimaryButton> : null}
      />
      <div className="d-flex flex-wrap gap-2 mb-3">
        <input className="form-control" style={{ ...inputStyle, maxWidth: 320 }} placeholder="Search suppliers" aria-label="Search suppliers" value={typed} onChange={(e) => setTyped(e.target.value)} />
        <div style={{ minWidth: 200 }}>
          <AppDropdown id="proc-supplier-status" value={status} options={STATUS_FILTERS} onChange={(e) => setStatus(e.value ?? null)} aria-label="Status" />
        </div>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={rows}
          onRowClick={(r) => setOpenId(r.id)}
          empty={
            <EmptyState
              title={q || status ? "No suppliers match" : "No suppliers yet"}
              text={q || status ? "Try another search or status." : "Invite the suppliers you want to receive your RFQs. They get an email to set up their account."}
              action={canManage && !q && !status ? <PrimaryButton onClick={() => setInviting(true)}>Invite supplier</PrimaryButton> : null}
            />
          }
          columns={[
            {
              label: "Supplier",
              render: (r) => (
                <>
                  <div style={{ fontWeight: 600, color: "#111827" }}>{r.supplier_name}</div>
                  <div style={{ fontSize: 12, color: "#6B7280" }}>
                    {r.supplier_code}
                    {r.company_name && r.company_name !== r.supplier_name ? ` · ${r.company_name}` : ""}
                  </div>
                </>
              ),
            },
            { label: "Contact", render: (r) => r.invite_contact || r.supplier_email || "—" },
            {
              label: "Status",
              render: (r) => (r.invite_expired ? <ProcBadge status="EXPIRED" label="Invitation expired" /> : <ProcBadge status={r.status} />),
            },
            { label: "Documents", render: (r) => <ComplianceCell row={r} /> },
            { label: "Categories", render: (r) => r.category_count },
            { label: "Since", render: (r) => formatDate(r.created_at) },
          ]}
        />
      </LoadState>
      <InviteDrawer show={inviting} onHide={() => setInviting(false)} onSent={(res) => { setSent(res); reload(); }} />
      <InviteSent result={sent} onClose={() => setSent(null)} />
      {openId ? <SupplierPanel id={openId} canManage={canManage} onClose={() => setOpenId(null)} onChanged={reload} /> : null}
    </div>
  );
}
